/**
 * ReferrerReferralDetail
 * Shows detailed status of a single referral
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, User, Calendar, FileText, CheckCircle, Clock, 
  Mail, Phone, Hash, MapPin, AlertCircle
} from 'lucide-react';
import API_BASE_URL from '../config/api';

function ReferrerReferralDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const token = sessionStorage.getItem('referrerToken');

  const [referral, setReferral] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      navigate('/referrer/login');
      return;
    }
    fetchReferral();
  }, [id]);

  const fetchReferral = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/referrer-referrals/my-referrals/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.status === 401) {
        navigate('/referrer/login');
        return;
      }

      const data = await res.json();
      if (data.success) {
        setReferral(data.referral);
      } else {
        setError('Referral not found');
      }
    } catch (err) {
      setError('Unable to load referral details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-NZ', {
      day: 'numeric', month: 'long', year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-NZ', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true
    });
  };

  const getInjuryLabel = (site) => {
    const labels = {
      KNEE: 'Knee', HIP: 'Hip', SHOULDER: 'Shoulder', ANKLE: 'Ankle',
      FOOT: 'Foot', ELBOW: 'Elbow', WRIST: 'Wrist/Hand',
      LUMBAR_SPINE: 'Lumbar Spine', CERVICAL_SPINE: 'Cervical Spine',
      THORACIC_SPINE: 'Thoracic Spine', ACHILLES: 'Achilles',
      HAMSTRING: 'Hamstring', CONCUSSION: 'Concussion'
    };
    return labels[site] || site;
  };

  // Build status timeline
  const getTimeline = () => {
    if (!referral) return [];

    const steps = [
      {
        label: 'Referral Submitted',
        date: referral.createdAt,
        complete: true,
        icon: FileText,
      },
      {
        label: 'History Form Sent to Patient',
        date: referral.status !== 'submitted' ? referral.processedAt || referral.createdAt : null,
        complete: ['form_sent', 'form_completed', 'booked', 'completed'].includes(referral.status) || referral.patient?.formCompleted,
        icon: Mail,
      },
      {
        label: 'Patient Form Completed',
        date: referral.patient?.formCompletedAt,
        complete: referral.patient?.formCompleted || false,
        icon: CheckCircle,
      },
      {
        label: 'Appointment Booked',
        date: referral.patient?.selectedSlot,
        complete: ['confirmed', 'booked'].includes(referral.patient?.appointmentStatus),
        icon: Calendar,
      },
    ];

    return steps;
  };

  const fieldStyle = {
    display: 'flex', 
    marginBottom: 10,
    fontSize: 14,
  };

  const fieldLabelStyle = {
    fontWeight: 600, 
    color: '#555', 
    width: 140, 
    flexShrink: 0,
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f0f4f8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#999' }}>Loading...</p>
      </div>
    );
  }

  if (error || !referral) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f0f4f8', padding: 20 }}>
        <div style={{ maxWidth: 500, margin: '60px auto', textAlign: 'center' }}>
          <AlertCircle size={48} color="#c33" style={{ marginBottom: 15 }} />
          <p style={{ color: '#c33' }}>{error || 'Referral not found'}</p>
          <button onClick={() => navigate('/referrer/dashboard')}
            style={{ marginTop: 15, padding: '10px 20px', backgroundColor: '#6b9bd1', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const timeline = getTimeline();

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f0f4f8' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #6b9bd1 0%, #4a7fb5 100%)',
        padding: '15px 20px',
        display: 'flex', alignItems: 'center', gap: 15,
      }}>
        <button
          onClick={() => navigate('/referrer/dashboard')}
          style={{
            backgroundColor: 'rgba(255,255,255,0.15)',
            color: 'white', border: 'none', borderRadius: 6,
            padding: '8px 12px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          <ArrowLeft size={16} /> Back
        </button>
        <h1 style={{ color: 'white', margin: 0, fontSize: 20, fontWeight: 700 }}>
          Referral Details
        </h1>
      </div>

      <div style={{ maxWidth: 600, margin: '0 auto', padding: 20 }}>
        {/* Status Timeline */}
        <div style={{
          backgroundColor: 'white', borderRadius: 8, padding: 20,
          marginBottom: 15, border: '1px solid #eee',
        }}>
          <h3 style={{ margin: '0 0 20px', fontSize: 16, color: '#333' }}>
            Referral Progress
          </h3>

          {timeline.map((step, index) => {
            return (
              <div key={index} style={{ display: 'flex', gap: 12, marginBottom: index < timeline.length - 1 ? 0 : 0 }}>
                {/* Timeline line and dot */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 24 }}>
                  <div style={{
                    width: 24, height: 24, borderRadius: '50%',
                    backgroundColor: step.complete ? '#5cb85c' : '#e0e0e0',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    {step.complete ? (
                      <CheckCircle size={14} color="white" />
                    ) : (
                      <Clock size={12} color="#999" />
                    )}
                  </div>
                  {index < timeline.length - 1 && (
                    <div style={{
                      width: 2, flex: 1, minHeight: 30,
                      backgroundColor: step.complete ? '#5cb85c' : '#e0e0e0',
                    }} />
                  )}
                </div>

                {/* Step content */}
                <div style={{ paddingBottom: index < timeline.length - 1 ? 15 : 0 }}>
                  <div style={{
                    fontWeight: 600, fontSize: 14,
                    color: step.complete ? '#333' : '#999',
                  }}>
                    {step.label}
                  </div>
                  {step.date && (
                    <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>
                      {formatDateTime(step.date)}
                    </div>
                  )}
                  {!step.complete && !step.date && (
                    <div style={{ fontSize: 12, color: '#bbb', marginTop: 2 }}>
                      Pending
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Patient Details */}
        <div style={{
          backgroundColor: 'white', borderRadius: 8, padding: 20,
          marginBottom: 15, border: '1px solid #eee',
        }}>
          <h3 style={{ margin: '0 0 15px', fontSize: 16, color: '#333', display: 'flex', alignItems: 'center', gap: 8 }}>
            <User size={18} color="#6b9bd1" /> Patient Details
          </h3>

          <div style={fieldStyle}>
            <span style={fieldLabelStyle}>Name:</span>
            <span style={{ color: '#333' }}>{referral.patientName}</span>
          </div>
          <div style={fieldStyle}>
            <span style={fieldLabelStyle}>Date of Birth:</span>
            <span style={{ color: '#333' }}>{formatDate(referral.patientDOB)}</span>
          </div>
          <div style={fieldStyle}>
            <span style={fieldLabelStyle}>Email:</span>
            <span style={{ color: '#333' }}>{referral.patientEmail}</span>
          </div>
          <div style={fieldStyle}>
            <span style={fieldLabelStyle}>Phone:</span>
            <span style={{ color: '#333' }}>{referral.patientPhone}</span>
          </div>
          {referral.nhi && (
            <div style={fieldStyle}>
              <span style={fieldLabelStyle}>NHI:</span>
              <span style={{ color: '#333' }}>{referral.nhi}</span>
            </div>
          )}
        </div>

        {/* Injury & ACC Details */}
        <div style={{
          backgroundColor: 'white', borderRadius: 8, padding: 20,
          marginBottom: 15, border: '1px solid #eee',
        }}>
          <h3 style={{ margin: '0 0 15px', fontSize: 16, color: '#333', display: 'flex', alignItems: 'center', gap: 8 }}>
            <MapPin size={18} color="#e8941c" /> Injury & ACC
          </h3>

          <div style={fieldStyle}>
            <span style={fieldLabelStyle}>Injury Site:</span>
            <span style={{ color: '#333' }}>
              {getInjuryLabel(referral.injurySite)}
              {referral.injurySide && ` (${referral.injurySide.charAt(0).toUpperCase() + referral.injurySide.slice(1)})`}
            </span>
          </div>
          {referral.accNumber && (
            <div style={fieldStyle}>
              <span style={fieldLabelStyle}>ACC Number:</span>
              <span style={{ color: '#333' }}>{referral.accNumber}</span>
            </div>
          )}
          {referral.dateOfInjury && (
            <div style={fieldStyle}>
              <span style={fieldLabelStyle}>Date of Injury:</span>
              <span style={{ color: '#333' }}>{formatDate(referral.dateOfInjury)}</span>
            </div>
          )}
        </div>

        {/* Clinical Details */}
        <div style={{
          backgroundColor: 'white', borderRadius: 8, padding: 20,
          marginBottom: 15, border: '1px solid #eee',
        }}>
          <h3 style={{ margin: '0 0 15px', fontSize: 16, color: '#333', display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileText size={18} color="#5cb85c" /> Clinical Details
          </h3>

          <div style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 600, color: '#555', fontSize: 13, marginBottom: 5 }}>Reason for Referral:</div>
            <div style={{ color: '#333', fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
              {referral.reasonForReferral}
            </div>
          </div>

          {referral.clinicalNotes && (
            <div>
              <div style={{ fontWeight: 600, color: '#555', fontSize: 13, marginBottom: 5 }}>Clinical Notes:</div>
              <div style={{ color: '#333', fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                {referral.clinicalNotes}
              </div>
            </div>
          )}
        </div>

        {/* Documents */}
        {referral.documents && referral.documents.length > 0 && (
          <div style={{
            backgroundColor: 'white', borderRadius: 8, padding: 20,
            marginBottom: 15, border: '1px solid #eee',
          }}>
            <h3 style={{ margin: '0 0 15px', fontSize: 16, color: '#333' }}>
              Uploaded Documents ({referral.documents.length})
            </h3>

            {referral.documents.map(doc => (
              <div key={doc.id} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 12px', backgroundColor: '#f5f5f5',
                borderRadius: 6, marginBottom: 6, fontSize: 13,
              }}>
                <FileText size={16} color="#6b9bd1" />
                <span style={{ flex: 1 }}>{doc.fileName}</span>
                {doc.fileSize && (
                  <span style={{ color: '#999', fontSize: 12 }}>
                    {(doc.fileSize / 1024).toFixed(0)} KB
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ReferrerReferralDetail;
